import { abbreviations as TZDB_ABBREVIATIONS, getTimeZones } from '@vvo/tzdb';

export interface TimezoneOption {
  city: string;
  ianaName: string;
  alternativeName: string;
  abbreviation: string;
  countryName: string;
  countryCode: string;
  group: readonly string[];
  gmtLabel: string;
  currentOffsetMinutes: number;
  rawOffsetMinutes: number;
  searchCorpus: string;
  isSystemTimezone: boolean;
}

/** @internal */
export function getSeasonalAbbreviation(ianaName: string, date: Date): string {
  const longName =
    new Intl.DateTimeFormat('en-US', { timeZone: ianaName, timeZoneName: 'long' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return TZDB_ABBREVIATIONS[longName] ?? '';
}

/** @internal */
export function getAbbreviations(ianaName: string, currentAbbreviation: string): string[] {
  try {
    // In case some state/nation modifies their use of "Daylight time" in the future,
    // checking the current year to adapt if an abbreviation gets added or removed.
    const year = new Date().getFullYear();
    const summerAbbr = getSeasonalAbbreviation(ianaName, new Date(`${year}-07-01`));
    const winterAbbr = getSeasonalAbbreviation(ianaName, new Date(`${year}-01-01`));
    return [...new Set([currentAbbreviation, summerAbbr, winterAbbr].filter(Boolean))];
  } catch {
    return [currentAbbreviation];
  }
}

/** @internal */
export function formatGmtLabel(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  return `GMT ${sign}${hours}:${mins.toString().padStart(2, '0')}`;
}

function buildAllCityOptions(): TimezoneOption[] {
  const systemIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zones = getTimeZones({ includeUtc: true });
  const options: TimezoneOption[] = [];

  for (const zone of zones) {
    const gmtLabel = formatGmtLabel(zone.currentTimeOffsetInMinutes);
    const isSystemTimezone = zone.name === systemIana;

    for (const city of zone.mainCities) {
      if (!city.trim()) continue;
      options.push({
        city,
        ianaName: zone.name,
        alternativeName: zone.alternativeName,
        abbreviation: zone.abbreviation,
        countryName: zone.countryName,
        countryCode: zone.countryCode,
        group: zone.group,
        gmtLabel,
        currentOffsetMinutes: zone.currentTimeOffsetInMinutes,
        rawOffsetMinutes: zone.rawOffsetInMinutes,
        searchCorpus: [
          city,
          zone.countryName,
          zone.alternativeName,
          ...getAbbreviations(zone.name, zone.abbreviation),
          zone.name,
          gmtLabel,
        ]
          .join(' ')
          .toLowerCase(),
        isSystemTimezone,
      });
    }
  }

  const sortKey = (city: string): string => city.replace(/^[^a-zA-Z\u00C0-\u024F]+/, '');
  options.sort((a, b) => sortKey(a.city).localeCompare(sortKey(b.city), undefined, { sensitivity: 'base' }));

  return options;
}

export const SYSTEM_TIMEZONE_IANA: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const UTC_TIMEZONE_OPTION: Readonly<TimezoneOption> = Object.freeze({
  city: 'UTC',
  ianaName: 'UTC',
  alternativeName: 'Coordinated Universal Time',
  abbreviation: 'UTC',
  countryName: '',
  countryCode: '',
  group: Object.freeze(['UTC']),
  gmtLabel: 'GMT +0:00',
  currentOffsetMinutes: 0,
  rawOffsetMinutes: 0,
  searchCorpus: 'utc coordinated universal time gmt +0:00',
  isSystemTimezone: false,
});

function buildSystemTimezoneOption(): Readonly<TimezoneOption> {
  const systemIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zones = getTimeZones({ includeUtc: true });
  const zone = zones.find((z) => z.name === systemIana);

  if (zone) {
    const gmtLabel = formatGmtLabel(zone.currentTimeOffsetInMinutes);
    return Object.freeze({
      city: 'Your timezone',
      ianaName: zone.name,
      alternativeName: zone.alternativeName,
      abbreviation: zone.abbreviation,
      countryName: zone.countryName,
      countryCode: zone.countryCode,
      group: Object.freeze(zone.group),
      gmtLabel,
      currentOffsetMinutes: zone.currentTimeOffsetInMinutes,
      rawOffsetMinutes: zone.rawOffsetInMinutes,
      searchCorpus: ['your timezone', zone.countryName, zone.alternativeName, zone.abbreviation, zone.name, gmtLabel]
        .join(' ')
        .toLowerCase(),
      isSystemTimezone: true,
    });
  }

  return Object.freeze({
    ...UTC_TIMEZONE_OPTION,
    city: 'Your timezone',
    searchCorpus: 'your timezone utc coordinated universal time gmt +0:00',
    isSystemTimezone: true,
  });
}

export const SYSTEM_TIMEZONE_OPTION: Readonly<TimezoneOption> = buildSystemTimezoneOption();

export const TIMEZONE_OPTIONS: readonly TimezoneOption[] = Object.freeze(buildAllCityOptions());
